<script setup>
/**
 * @todo When the legacy theme is removed: delete the legacyProps prop, the useTheme import,
 * and collapse effectiveProps to { ...base, unelevated: true, ...attrs }.
 */

import { computed, ref, useAttrs, useSlots } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * M3 dropdown button that opens a floating menu on click.
 *
 * @status recommended
 * @group actions
 * @useWhen Presenting a primary button that reveals a contextual dropdown menu when clicked
 * @see https://quasar.dev/vue-components/button-dropdown Quasar q-btn-dropdown API
 */
defineOptions({ name: 'HrDropdownMenu' });

const props = defineProps({
  /**
   * Visual emphasis variant derived from the M3 hierarchy.
   * @values filled, tonal, elevated, outlined, text
   */
  variant: {
    type: String,
    default: 'filled',
    validator: (v) => ['filled', 'tonal', 'elevated', 'outlined', 'text'].includes(v),
  },
  /**
   * Size of the button, which affects padding and font size.
   * @values xs, sm, md, lg, xl
   */
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(v),
  },
  /**
   * The text label of the button.
   */
  label: {
    type: String,
    default: null,
  },
  /**
   * The icon to display on the button.
   */
  icon: {
    type: String,
    default: null,
  },
  /**
   * Whether the button is disabled.
   */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether the button is in a loading state.
   */
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * Override the dropdown caret icon.
   */
  dropdownIcon: {
    type: String,
    default: null,
  },
  /**
   * Prevent the caret icon from rotating when the menu opens.
   */
  noIconAnimation: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props are any additional Quasar props that can be passed to the button for backwards
   * compatibility with the legacy theme. These props will be ignored when a design system theme
   * is active.
   * @deprecated This prop is only for backwards compatibility with the legacy theme and should be
   * removed once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const slots = useSlots();
const { hasTheme } = useTheme();

const qBtnDropdownRef = ref(null);
defineExpose({
  /** Opens the dropdown menu. */
  show: () => qBtnDropdownRef.value?.show(),
  /** Closes the dropdown menu. */
  hide: () => qBtnDropdownRef.value?.hide(),
});

const hasLeading = computed(() => props.icon !== null || !!slots.avatar);

const classes = computed(() => [
  'hr-dropdown-menu',
  `hr-dropdown-menu--${props.variant}`,
  `hr-dropdown-menu--size-${props.size}`,
  hasLeading.value && 'hr-dropdown-menu--has-leading',
].filter(Boolean));

const effectiveProps = computed(() => {
  // When a custom #label slot is being rendered (avatar or label slot provided),
  // the label text is already inside that slot — don't also pass it as a prop
  // or q-btn will render it a second time independently of the slot content.
  const passLabelProp = props.label !== null && !slots.label && !slots.avatar;
  const base = {
    ...(passLabelProp && { label: props.label }),
    ...(props.icon !== null && { icon: props.icon }),
    ...(props.dropdownIcon !== null && { dropdownIcon: props.dropdownIcon }),
    noIconAnimation: props.noIconAnimation,
    disable: props.disable,
    loading: props.loading,
  };

  if (hasTheme.value) {
    return {
      ...base,
      unelevated: true,
      ...attrs,
      contentClass: ['hr-dropdown-menu__popup', attrs.contentClass].filter(Boolean),
    };
  }
  return { ...base, size: props.size, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <q-btn-dropdown
    ref="qBtnDropdownRef"
    v-bind="effectiveProps"
    :class="hasTheme ? classes : undefined"
  >
    <template
      v-if="$slots.label || $slots.avatar"
      #label="slotProps"
    >
      <div class="hr-dropdown-menu__label row items-center no-wrap">
        <!-- @slot avatar Leading image or avatar shown before the label. -->
        <slot name="avatar" />
        <!-- @slot label Custom button label content; receives q-btn-dropdown label slot props. -->
        <slot
          v-if="$slots.label"
          name="label"
          v-bind="slotProps ?? {}"
        />
        <span v-else>{{ props.label }}</span>
      </div>
    </template>
    <template #default>
      <!-- @slot default Dropdown panel content. -->
      <slot />
    </template>
    <template #loading>
      <!-- @slot loading Custom loading indicator shown while `loading` is true. -->
      <slot name="loading" />
    </template>
  </q-btn-dropdown>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

.hr-dropdown-menu {
  border-radius: var(--app-shape-corner-full);
  text-transform: none;
}

/* ── Variants ───────────────────────────────────────────────────── */

.hr-dropdown-menu--filled {
  background: var(--app-primary);
  color: var(--app-on-primary);
}

.hr-dropdown-menu--tonal {
  background: var(--app-secondary-container);
  color: var(--app-on-secondary-container);
}

.hr-dropdown-menu--elevated {
  background: var(--app-surface-container-low);
  color: var(--app-primary);
  box-shadow: var(--app-elevation-1);
}

.hr-dropdown-menu--outlined {
  background: transparent;
  color: var(--app-primary);
  border: 1px solid var(--app-outline);
}

.hr-dropdown-menu--text {
  background: transparent;
  color: var(--app-primary);
}

/* ── Sizes ──────────────────────────────────────────────────────── */

/* Trailing always uses icon-adjacent 12dp (chevron present); leading stays at standard padding. */
/* When a leading icon/avatar is present, leading also tightens to match trailing. */

.hr-dropdown-menu--size-xs {
  min-height: var(--app-comp-button-container-height-xs);
  padding: 0 8px 0 var(--app-comp-button-container-padding-xs);
  font-size: var(--app-comp-button-label-text-size-xs);
}

.hr-dropdown-menu--size-sm {
  min-height: var(--app-comp-button-container-height-sm);
  padding: 0 10px 0 var(--app-comp-button-container-padding-sm);
  font-size: var(--app-comp-button-label-text-size-sm);
}

.hr-dropdown-menu--size-md {
  min-height: var(--app-comp-button-container-height-md);
  padding: 0 12px 0 var(--app-comp-button-container-padding-md);
  font-size: var(--app-comp-button-label-text-size-md);
}

.hr-dropdown-menu--size-lg {
  min-height: var(--app-comp-button-container-height-lg);
  padding: 0 14px 0 var(--app-comp-button-container-padding-lg);
  font-size: var(--app-comp-button-label-text-size-lg);
}

.hr-dropdown-menu--size-xl {
  min-height: var(--app-comp-button-container-height-xl);
  padding: 0 16px 0 var(--app-comp-button-container-padding-xl);
  font-size: var(--app-comp-button-label-text-size-xl);
}

.hr-dropdown-menu--has-leading.hr-dropdown-menu--size-xs { padding-left: 8px; }
.hr-dropdown-menu--has-leading.hr-dropdown-menu--size-sm { padding-left: 10px; }
.hr-dropdown-menu--has-leading.hr-dropdown-menu--size-md { padding-left: 12px; }
.hr-dropdown-menu--has-leading.hr-dropdown-menu--size-lg { padding-left: 14px; }
.hr-dropdown-menu--has-leading.hr-dropdown-menu--size-xl { padding-left: 16px; }

/* ── State layer ────────────────────────────────────────────────── */

.hr-dropdown-menu :deep(.q-focus-helper) { display: none; }

.hr-dropdown-menu::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms linear;
}

.hr-dropdown-menu--filled::before          { background: var(--app-on-primary); }
.hr-dropdown-menu--tonal::before           { background: var(--app-on-secondary-container); }
.hr-dropdown-menu--elevated::before,
.hr-dropdown-menu--outlined::before,
.hr-dropdown-menu--text::before            { background: var(--app-primary); }

.hr-dropdown-menu:hover::before            { opacity: 0.08; }

/* ── Disabled ───────────────────────────────────────────────────── */

.hr-dropdown-menu.disabled {
  background: color-mix(in srgb, var(--app-on-surface) 12%, transparent);
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

.hr-dropdown-menu--outlined.disabled {
  border-color: color-mix(in srgb, var(--app-on-surface) 12%, transparent);
}

</style>

<style lang="scss">

/*
 * Not scoped — Quasar teleports the dropdown panel outside the component DOM,
 * so scoped styles cannot reach it. The [data-theme] guard ensures these rules
 * only apply when a design system theme is active.
 */

[data-theme] .hr-dropdown-menu__popup {
  background: var(--app-surface-container);
  box-shadow: var(--app-elevation-2);
}

</style>
